import { useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, cents, useCurrentPartner, useMe } from '../../lib'
import { ErrorBox, Loading } from '../../app/ui'
import type { ChecklistItem, Grant, Setup } from './types'

export const LABELS: Record<string, string> = {
  profile: 'Your company',
  verify: 'Company details',
  spend_source: 'Send us spend',
  policy: 'Set your points',
  billing: 'How you pay',
  terms: 'Accept the terms',
  team: 'Invite your team',
  website_button: 'Website button',
}

export const LINKS: Record<string, string> = {
  profile: '/partner/verify',
  verify: '/partner/verify',
  spend_source: '/partner/spend',
  policy: '/partner/rules',
  billing: '/partner/billing',
  terms: '/partner/review',
  team: '/partner/team',
  website_button: '/partner/button',
}

export const STATUS_LABEL: Record<string, string> = {
  draft: 'Setting up',
  pending_review: 'Waiting for us',
  live: 'Live',
  paused: 'Paused',
}

/** "Months 2–6 → 1 point per $1" */
export function ruleText(r: { from: number; to: number | null; rate: string }, only: boolean): string {
  const when = r.to === null ? (only ? 'Every month' : `Month ${r.from}+`) : r.from === r.to ? `Month ${r.from}` : `Months ${r.from}–${r.to}`
  const n = r.rate.includes('.') ? r.rate.replace(/0+$/, '').replace(/\.$/, '') : r.rate
  return `${when} → ${n} ${n === '1' ? 'point' : 'points'} per $1`
}

export const PRICE_COPY = 'You pay $0.01 per point your customers earn.'

export function GrantCopy({ grant }: { grant: Grant }) {
  if (!grant.cap_cents) return null   // no starter budget: "the first $0.00" says nothing
  return (
    <p data-testid="partners-grant-copy">
      We cover the first {cents(grant.cap_cents)} of points: used {cents(grant.used_cents)}, left {cents(grant.left_cents)}.
    </p>
  )
}

/** Wraps a partner page: needs a signed-in partner member. */
export function PartnerPage({ children }: { children: (pid: string, role: string) => ReactNode }) {
  const { me, isLoading } = useMe()
  const partner = useCurrentPartner()
  if (isLoading) return <Loading />
  if (!me?.user) {
    return (
      <div className="page narrow">
        <p>
          <Link to="/login">Sign in</Link> to manage your partner account.
        </p>
      </div>
    )
  }
  if (!partner) {
    return (
      <div className="page narrow stack" data-testid="partners-no-partner">
        <h1>Partner portal</h1>
        <p className="muted">You are not on a partner team yet.</p>
        <Link className="button primary" to="/partner/new" data-testid="partners-start-signup">
          Sign up as a partner
        </Link>
      </div>
    )
  }
  return <>{children(partner.id, partner.role)}</>
}

export function useSetup(pid: string) {
  return useQuery({ queryKey: ['partners', 'setup', pid], queryFn: () => api<Setup>('GET', `/api/partners/${pid}/setup`) })
}

export function useInvalidate() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['partners'] })
}

/** A mutation that refreshes partner queries on success and keeps its last error. */
export function useAction<A, R = unknown>(fn: (a: A) => Promise<R>, onDone?: (r: R) => void) {
  const inval = useInvalidate()
  const [error, setError] = useState<unknown>(null)
  const m = useMutation({
    mutationFn: fn,
    onMutate: () => setError(null),
    onError: (e) => setError(e),
    onSuccess: (r) => {
      inval()
      onDone?.(r)
    },
  })
  return { run: m.mutate, busy: m.isPending, error, done: m.isSuccess, data: m.data }
}

/** The due-now steps as Home lists them: Company details also needs Your company done. */
function dueSteps(items: ChecklistItem[]): ChecklistItem[] {
  const profile = items.find((c) => c.item === 'profile')
  return items
    .filter((c) => c.due_now && c.item !== 'profile')
    .map((c) => (c.item === 'verify' && profile ? { ...c, done: c.done && profile.done } : c))
}

/** Where Next goes from `step`: the next open set-up step in LABELS order (Team is optional, so never), wrapping to
 * an earlier open one; with none open, Send for review. */
export function nextStep(items: ChecklistItem[], step: string): { label: string; to: string } {
  const due = dueSteps(items)
  const order = Object.keys(LABELS).filter((k) => k !== 'profile' && k !== 'team' && due.some((c) => c.item === k))
  const open = (k: string) => k !== step && !due.find((c) => c.item === k)!.done
  const at = order.indexOf(step)
  const next = [...order.slice(at + 1), ...order.slice(0, Math.max(at, 0))].find(open)
  return next ? { label: LABELS[next], to: LINKS[next] } : { label: 'Send for review', to: LINKS.terms }
}

/** Back to Home and Next to the next open step. Forms disable Next until changes are saved. */
export function SetupFooter({ pid, step, disabled }: { pid: string; step: string; disabled?: boolean }) {
  const s = useSetup(pid)
  const nav = useNavigate()
  if (s.data?.partner.status !== 'draft') return null
  const next = step === 'terms' ? null : nextStep(s.data.checklist, step)
  const go = () => next && nav(next.to)
  return (
    <nav className="setup-footer" aria-label="Set-up steps" data-testid="partners-setup-footer">
      <Link to="/partner" data-testid="partners-setup-back">← Back</Link>
      {next && (
        <button type="button" className="primary" data-testid="partners-setup-next"
          disabled={disabled} onClick={go}>
          Next: {next.label} →
        </button>
      )}
    </nav>
  )
}

export function Checklist({ items }: { items: ChecklistItem[] }) {
  const shown = dueSteps(items)
  return (
    <ul className="stack" style={{ listStyle: 'none', padding: 0 }} data-testid="partners-checklist">
      {shown.map((c) => (
        <li key={c.item} className="row" data-testid={`partners-check-${c.item}`} data-done={c.done ? 'yes' : 'no'}>
          <span className={c.done ? 'tag status-earning' : 'tag'}>{c.done ? 'Done' : 'To do'}</span>
          <Link to={LINKS[c.item] ?? '/partner'}>{LABELS[c.item] ?? c.item}</Link>
        </li>
      ))}
    </ul>
  )
}

export function Err({ error }: { error: unknown }) {
  return error ? <ErrorBox error={error} /> : null
}
